import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { AlertIcon } from "@/components/ui/icons";

/**
 * The token is fine; the server did not answer.
 *
 * The shell is still around this — sidebar, top bar, theme. That is the point:
 * the panel has not fallen over, one request did, and a screen that throws the
 * whole interface away to say so makes a blip look like an outage.
 *
 * Deliberately says nothing technical. No url, no status code, no mention of
 * CORS or `/me`. Those belong in the console, where the person who can act on
 * them is already looking; on screen they turn a two-sentence problem into
 * something that reads like the product is broken.
 *
 * It retries on its own, backing off, because most of these clear up without
 * anybody doing anything — a server restarting, a laptop waking. The button is
 * for the impatient, and the countdown is there so the screen does not look
 * inert while it waits.
 */

/** Seconds between attempts. Caps rather than growing forever. */
const SCHEDULE = [5, 10, 20, 30, 60] as const;

interface Props {
  onRetry: () => void;
  retrying: boolean;
}

export function ConnectionProblem({ onRetry, retrying }: Props) {
  /*
    The countdown lives in refs; state is only what the label shows.

    The timer is a state machine with three moving parts — how long is left,
    which step of the backoff we are on, and when to fire — and running them
    through `useState` means the effect that advances them has to list them as
    dependencies, which tears the interval down and rebuilds it on every tick.
    Keeping them in refs lets one interval own the whole thing, and the single
    `setSeconds` is a mirror for rendering rather than the source of truth.

    `onRetry` is a ref for a related reason: the caller passes an inline arrow,
    so its identity changes on every render of the shell, and in a dependency
    list that alone would restart the countdown continuously.
  */
  const retryRef = useRef(onRetry);
  useEffect(() => {
    retryRef.current = onRetry;
  });

  const remainingRef = useRef<number>(SCHEDULE[0]);
  const attemptRef = useRef(0);
  const [seconds, setSeconds] = useState<number>(SCHEDULE[0]);

  // Paused while a request is actually in flight, so the countdown is never
  // racing the attempt it is counting down to.
  useEffect(() => {
    if (retrying) return;

    const timer = setInterval(() => {
      remainingRef.current -= 1;

      if (remainingRef.current <= 0) {
        attemptRef.current = Math.min(attemptRef.current + 1, SCHEDULE.length - 1);
        remainingRef.current = SCHEDULE[attemptRef.current]!;
        retryRef.current();
      }

      setSeconds(remainingRef.current);
    }, 1000);

    return () => clearInterval(timer);
  }, [retrying]);

  // Asking by hand restarts the wait from the current step rather than letting
  // a click land a second request on top of one that is about to fire anyway.
  const retryNow = useCallback(() => {
    remainingRef.current = SCHEDULE[attemptRef.current]!;
    setSeconds(remainingRef.current);
    retryRef.current();
  }, []);

  return (
    <Card>
      <CardBody className="px-6 py-14 text-center sm:px-10">
        <span
          aria-hidden="true"
          className="bg-warning-soft text-warning mx-auto flex size-12 items-center justify-center rounded-full"
        >
          <AlertIcon className="size-5" />
        </span>

        <h2 className="text-content mt-5 text-lg font-semibold tracking-tight">
          We can't load your workspace right now
        </h2>

        <p className="text-content-muted mx-auto mt-2.5 max-w-md text-sm leading-relaxed">
          The service isn't responding. Nothing you've done has been lost — this page will fill in
          by itself once it's back.
        </p>

        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
          <Button onClick={retryNow} disabled={retrying}>
            {retrying ? "Checking…" : "Try again"}
          </Button>

          {/* `aria-hidden`: a number that changes every second is unusable
              read aloud, and the button beside it offers the same action at
              any moment. The non-breaking space holds the row's height while
              a request is in flight. */}
          <span aria-hidden="true" className="text-content-subtle text-xs">
            {retrying ? " " : `Retrying automatically in ${String(seconds)}s`}
          </span>
        </div>
      </CardBody>
    </Card>
  );
}
